/**
 * Fixed score-range presentation buckets (spec 1 section 7). These ranges
 * are intentionally NOT configurable -- unlike scoring thresholds inside a
 * criterion's config, the list/detail color coding is a fixed UI contract.
 */
export const EVALUATION_STATUS = {
    IN_PROGRESS: "IN_PROGRESS",
    SCORED: "SCORED",
    NEEDS_REVIEW: "NEEDS_REVIEW",
    DISQUALIFIED: "DISQUALIFIED",
};

const BUCKETS = [
    { min: 80, max: 100, color: "green.400", labelKey: "bucketRecommended" },
    { min: 60, max: 79.999, color: "yellow.400", labelKey: "bucketToReview" },
    { min: 40, max: 59.999, color: "orange.400", labelKey: "bucketLow" },
    { min: 0, max: 39.999, color: "red.400", labelKey: "bucketVeryLow" },
];

/**
 * Resolves a candidate/conversation evaluation to a { color, labelKey } bucket.
 * Disqualification is always visually dominant, regardless of scorePercent.
 * Returns null when there's no evaluation yet (caller must render a distinct
 * "no data" state, never fall back to a 0-point bucket).
 *
 * @param {{status?: string, scorePercent?: number|null} | null | undefined} evaluation
 */
export function getScoreBucket(evaluation) {
    if (!evaluation) return null;
    if (evaluation.status === EVALUATION_STATUS.DISQUALIFIED) {
        return { color: "red.500", labelKey: "bucketDisqualified", dominant: true };
    }
    if (evaluation.status === EVALUATION_STATUS.IN_PROGRESS) return null;
    if (evaluation.status === EVALUATION_STATUS.NEEDS_REVIEW) {
        return { color: "text.grey", labelKey: "bucketNeedsReview", dominant: true };
    }
    if (evaluation.scorePercent == null) return null;
    const pct = Math.min(100, Math.max(0, evaluation.scorePercent));
    return BUCKETS.find((b) => pct >= b.min && pct <= b.max) ?? null;
}
